/**
 * La aritmetica del calendario: que dia cae en que casilla y con cuanta fuerza
 * se pinta cada una. Sin React y sin DOM, para poder leerla de un vistazo.
 *
 * Todas las fechas son cadenas `yyyy-MM-dd` del servidor, que ya son el dia
 * local del usuario (SPEC 19.19). Aqui no se convierte ninguna zona horaria:
 * un `Date` solo se usa para preguntar en que dia de la semana cae una fecha
 * civil y cuantos dias tiene un mes, que no dependen de ninguna zona.
 */

export type MonthRef = { year: number; month: number };

/**
 * La semana empieza en LUNES, no en domingo: la semana de trabajo de un mercado
 * es de lunes a viernes, y con el domingo delante el fin de semana queda partido
 * en los dos extremos de la fila.
 */
export const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"] as const;

/** El nombre largo del dia, en el mismo orden que `WEEKDAYS`: lunes primero. */
export const WEEKDAY_NAMES = [
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
] as const;

export function shiftMonth(ref: MonthRef, months: number): MonthRef {
  const index = ref.year * 12 + (ref.month - 1) + months;

  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

export function sameMonth(a: MonthRef, b: MonthRef): boolean {
  return a.year === b.year && a.month === b.month;
}

export function isoDate(year: number, month: number, day: number): string {
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

export function parseIsoDate(text: string): { year: number; month: number; day: number } | null {
  const parts = text.split("-");
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;

  return { year, month, day };
}

/** Una casilla de la rejilla. `date` en nulo es un hueco de relleno, no un dia. */
export type Cell = { key: string; date: string | null; day: number; weekday: string };

/**
 * Las casillas del mes, con los huecos del principio para que el dia 1 caiga en
 * su columna. No se rellena el final: una ultima fila corta se lee igual de bien
 * y las casillas vacias de mas solo ocupan sitio en el movil.
 */
export function monthCells(ref: MonthRef): Cell[] {
  const first = new Date(ref.year, ref.month - 1, 1);
  const lead = (first.getDay() + 6) % 7;
  const length = new Date(ref.year, ref.month, 0).getDate();

  const cells: Cell[] = [];

  for (let i = 0; i < lead; i += 1) {
    cells.push({ key: `hueco-${i}`, date: null, day: 0, weekday: "" });
  }

  for (let day = 1; day <= length; day += 1) {
    const date = isoDate(ref.year, ref.month, day);

    cells.push({
      key: date,
      date,
      day,
      weekday: WEEKDAY_NAMES[(lead + day - 1) % 7] ?? "",
    });
  }

  return cells;
}

/* ========================================================================== */
/* La escala de color                                                         */
/* ========================================================================== */

/**
 * El suelo de la escala es UNA UNIDAD DE RIESGO. Sin el, un mes cuyo mejor dia
 * fuera +0.2R lo pintaria a plena intensidad y se leeria como un mes redondo:
 * la escala diria "el mejor de este mes" cuando lo que tiene que decir es
 * "cuanto riesgo se movio ese dia".
 */
const REFERENCE_R = 1;

const MIN_ALPHA = 0.18;
const MAX_ALPHA = 0.75;

/**
 * La regla rota nunca baja de aqui aunque el dia valga cero R: el ambar tiene
 * que verse desde el otro lado de la pantalla (ADR 0003).
 */
const BREACH_MIN_ALPHA = 0.5;

export function heatScale(days: readonly { totalR: number }[]): number {
  return days.reduce((max, day) => Math.max(max, Math.abs(day.totalR)), REFERENCE_R);
}

/**
 * La intensidad crece con la RAIZ del R, no en linea recta. Con un +6R suelto en
 * el mes, una escala lineal dejaria los dias de 1R en un verde casi invisible y
 * el mapa se volveria un unico cuadro fuerte sobre un fondo plano. La raiz
 * levanta los dias normales sin que el dia grande deje de ser el mas fuerte.
 */
export function heatAlpha(totalR: number, scale: number): number {
  const ratio = scale <= 0 ? 0 : Math.min(1, Math.abs(totalR) / scale);

  return MIN_ALPHA + (MAX_ALPHA - MIN_ALPHA) * Math.sqrt(ratio);
}

export type HeatDay = {
  totalR: number;
  tradeCount: number;
  hasBrokenMandatoryRule: boolean;
};

/**
 * El fondo de una casilla, o `undefined` cuando no hay nada que pintar.
 *
 * **El ambar manda sobre el resultado.** Un dia que rompio una regla
 * innegociable se marca en ambar aunque haya ganado, porque el color mas fuerte
 * del producto mide DISCIPLINA y no dinero (ADR 0003, punto conservado de
 * SPEC 16). Verde sobre una regla rota seria felicitar la suerte.
 */
export function heatBackground(day: HeatDay, scale: number): string | undefined {
  if (day.tradeCount === 0) return undefined;

  const alpha = heatAlpha(day.totalR, scale);

  if (day.hasBrokenMandatoryRule) {
    return `rgba(var(--warn-rgb), ${Math.max(BREACH_MIN_ALPHA, alpha).toFixed(2)})`;
  }

  if (day.totalR > 0) return `rgba(var(--up-rgb), ${alpha.toFixed(2)})`;
  if (day.totalR < 0) return `rgba(var(--down-rgb), ${alpha.toFixed(2)})`;

  /* Cero R con operaciones no es "sin datos": el dia existio y quedo en tablas,
     o sus operaciones no llegaron a tener R. Un gris lo dice; el blanco no. */
  return "var(--surface-3)";
}

function pad(value: number, size: number): string {
  return String(value).padStart(size, "0");
}
